import { useState } from "react";
import { useLocation } from "react-router";
import { NavigationItemProps } from "../types/navigation.interface";

interface NavigationState {
  openItems: Set<string>;
}

export interface NavigationHookReturn {
  isItemActive: (item: NavigationItemProps) => boolean;
  isItemOpen: (key: string) => boolean;
  isCurrentLinkActive: (item: NavigationItemProps) => boolean;
  toggleItem: (key: string) => void;
  handleItemClick: (item: NavigationItemProps) => void;
  openItems: Set<string>;
}

const useAppNavigation = (): NavigationHookReturn => {
  const location = useLocation();
  const [state, setState] = useState<NavigationState>({
    openItems: new Set<string>(),
  });

  const isItemActive = (item: NavigationItemProps): boolean => {
    if (isCurrentLinkActive(item)) {
      return true;
    }

    if (item.children) {
      return item.children.some(child => isItemActive(child));
    }

    return false;
  };

  const isCurrentLinkActive = (item: NavigationItemProps): boolean => {
    return location.pathname === item.link;
  };

  const isItemOpen = (key: string): boolean => {
    return state.openItems.has(key);
  };

  const toggleItem = (key: string): void => {
    setState(prevState => {
      const newOpenItems = new Set(prevState.openItems);
      if (newOpenItems.has(key)) {
        newOpenItems.clear();
      } else {
        newOpenItems.clear();
        newOpenItems.add(key);
      }

      return { ...prevState, openItems: newOpenItems };
    });
  };

  const handleItemClick = (item: NavigationItemProps): void => {
    if (item.children?.length) {
      toggleItem(item.key);
    }
  };

  const { openItems } = state;

  return {
    isItemActive,
    isItemOpen,
    isCurrentLinkActive,
    toggleItem,
    handleItemClick,
    openItems,
  };
};

export default useAppNavigation;
